import React, { useEffect, useState, useMemo } from "react";
import AppShell from "@/components/app/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Sparkles, Star, Trash2, Edit3, RefreshCw, Rss, Search } from "lucide-react";

const CATEGORIES = [
  ["personal_credit_cards", "Personal Credit Cards"],
  ["business_credit_cards", "Business Credit Cards"],
  ["auto_loans", "Auto Loans"],
  ["business_funding", "Business Funding"],
  ["mortgages", "Mortgages"],
  ["credit_education", "Credit Education"],
  ["alerts", "Alerts"],
];

const MATCH_TAGS = ["Beginner", "Fair Credit", "Good Credit", "Excellent Credit", "Business Owner", "Funding Ready", "Needs More Work"];

export default function AdminFeed() {
  const [posts, setPosts] = useState([]);
  const [sources, setSources] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [published, setPublished] = useState("all");
  const [tab, setTab] = useState("posts");
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fetching, setFetching] = useState(false);

  const load = async () => {
    const params = {};
    if (q) params.q = q;
    if (cat !== "all") params.category = cat;
    if (published === "published") params.published = true;
    if (published === "unpublished") params.published = false;
    const { data } = await api.get("/admin/feed", { params });
    setPosts(data);
  };
  const loadSources = async () => {
    const { data } = await api.get("/admin/feed/sources");
    setSources(data);
  };
  useEffect(() => { load(); loadSources(); /* eslint-disable-next-line */ }, [cat, published]);

  const stats = useMemo(() => ({
    total: posts.length,
    published: posts.filter((p) => p.is_published).length,
    featured: posts.filter((p) => p.is_featured).length,
    manual: posts.filter((p) => p.source === "manual").length,
  }), [posts]);

  const fetchNow = async () => {
    setFetching(true);
    try {
      const { data } = await api.post("/admin/feed/fetch-now");
      toast.success(`Fetched: ${data.created} new · ${data.skipped} skipped`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFetching(false); }
  };

  const toggleFeature = async (p) => {
    try { await api.post(`/admin/feed/${p.id}/feature`); toast.success(p.is_featured ? "Unfeatured" : "Featured"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const togglePublish = async (p) => {
    await api.put(`/admin/feed/${p.id}`, { is_published: !p.is_published });
    toast.success(p.is_published ? "Unpublished" : "Published");
    load();
  };
  const del = async (id) => { await api.delete(`/admin/feed/${id}`); toast.success("Deleted"); load(); };

  return (
    <AppShell>
      <div className="flex items-end justify-between mb-6 cq-in flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-amber-600">Content Engine</p>
          <h1 className="font-display text-4xl font-black text-slate-900">Funding Feed</h1>
          <p className="text-slate-500 mt-1">Manual posts + automated RSS/AI ingestion. Every hour, new signals show up here.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchNow} disabled={fetching} className="rounded-xl h-11" data-testid="fetch-now-btn">
            <RefreshCw size={16} className={`mr-1 ${fetching ? "animate-spin" : ""}`} /> {fetching ? "Fetching…" : "Fetch now"}
          </Button>
          <Button onClick={() => setOpenAdd(true)} className="bg-amber-500 hover:bg-amber-600 rounded-xl h-11" data-testid="new-post-btn">
            <Plus size={16} className="mr-1" /> New post
          </Button>
          {openAdd && (
            <PostDialog
              onSaved={() => { setOpenAdd(false); load(); }}
              onClose={() => setOpenAdd(false)}
            />
          )}
        </div>
      </div>

      <div className="flex gap-2 mb-4">
        <TabButton active={tab === "posts"} onClick={() => setTab("posts")} label="Posts" count={posts.length} testid="tab-posts" />
        <TabButton active={tab === "sources"} onClick={() => setTab("sources")} label="Sources" count={sources.length} testid="tab-sources" />
      </div>

      {tab === "posts" && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <StatCard label="Total posts" value={stats.total} testid="stat-total" />
            <StatCard label="Published" value={stats.published} testid="stat-published" />
            <StatCard label="Featured" value={stats.featured} testid="stat-featured" />
            <StatCard label="Manual" value={stats.manual} testid="stat-manual" />
          </div>

          <Card className="p-4 sm:p-6 rounded-2xl border-slate-100">
            <div className="flex flex-col md:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input placeholder="Search titles & summaries…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-10 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="admin-feed-search" />
              </div>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="w-full md:w-56 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="admin-feed-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {CATEGORIES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={published} onValueChange={setPublished}>
                <SelectTrigger className="w-full md:w-40 h-11 rounded-xl bg-slate-50 border-slate-200" data-testid="admin-feed-published"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="unpublished">Unpublished</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={load} className="h-11 rounded-xl">Search</Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow><TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Source</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
                </TableHeader>
                <TableBody>
                  {posts.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-400 py-6">No posts yet.</TableCell></TableRow>}
                  {posts.map((p) => (
                    <TableRow key={p.id} data-testid={`admin-post-row-${p.id}`}>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-semibold text-slate-900 line-clamp-1">{p.title}</p>
                          <p className="text-xs text-slate-500 line-clamp-1">{p.summary}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">{CATEGORIES.find(([k]) => k === p.category)?.[1] || p.category}</TableCell>
                      <TableCell className="text-xs">{p.source === "rss" ? <Badge variant="outline" className="text-sky-700">RSS</Badge> : <Badge variant="outline" className="text-amber-700">Manual</Badge>} <span className="text-slate-500 ml-1">{p.source_name}</span></TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <Badge variant="outline" className={p.is_published ? "text-green-700 border-green-200" : "text-slate-500"}>{p.is_published ? "Published" : "Draft"}</Badge>
                          {p.is_featured && <Badge className="bg-amber-500 text-white">Featured</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="icon" variant="ghost" onClick={() => toggleFeature(p)} data-testid={`toggle-feature-${p.id}`}><Star size={16} className={p.is_featured ? "fill-amber-500 text-amber-500" : ""} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => togglePublish(p)} data-testid={`toggle-publish-${p.id}`}><Sparkles size={16} className={p.is_published ? "text-green-600" : "text-slate-400"} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`edit-post-${p.id}`}><Edit3 size={16} /></Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-post-${p.id}`}><Trash2 size={16} /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Delete post?</AlertDialogTitle><AlertDialogDescription>Removes "{p.title}".</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => del(p.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </>
      )}

      {tab === "sources" && <SourcesPanel sources={sources} reload={loadSources} />}

      {editing && <PostDialog post={editing} onSaved={() => { setEditing(null); load(); }} onClose={() => setEditing(null)} />}
    </AppShell>
  );
}

function TabButton({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-xl text-sm font-semibold ${active ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
      {label} <span className={`ml-1 text-[10px] rounded-full px-1.5 py-0.5 ${active ? "bg-white/20" : "bg-slate-100"}`}>{count}</span>
    </button>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <Card className="p-5 rounded-2xl border-slate-100">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-slate-400">{label}</p>
      <p className="font-display text-3xl font-black mt-1 text-slate-900" data-testid={testid}>{value}</p>
    </Card>
  );
}

function PostDialog({ post, onSaved, onClose }) {
  const isEdit = !!post;
  const [form, setForm] = useState(post || {
    title: "", category: "personal_credit_cards", summary: "", description: "",
    source_url: "", source_name: "", button_text: "Learn more", button_link: "",
    credit_score_range: "", recommended_for: [], is_featured: false, is_published: true,
    why_it_matters: "", who_should_care: "", next_step: "",
  });
  const [busy, setBusy] = useState(false);

  const toggleTag = (t) => setForm((f) => ({ ...f, recommended_for: f.recommended_for?.includes(t) ? f.recommended_for.filter((x) => x !== t) : [...(f.recommended_for || []), t] }));

  const submit = async () => {
    setBusy(true);
    try {
      if (isEdit) await api.put(`/admin/feed/${post.id}`, form);
      else await api.post("/admin/feed", form);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && (onClose ? onClose() : onSaved())}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="post-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? "Edit post" : "New feed post"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="post-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="post-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Recommended credit score</Label><Input value={form.credit_score_range || ""} onChange={(e) => setForm({ ...form, credit_score_range: e.target.value })} placeholder="670-850" data-testid="post-score" /></div>
          </div>
          <div><Label>Summary</Label><Textarea rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="post-summary" /></div>
          <div><Label>Full description</Label><Textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="post-description" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Source URL</Label><Input value={form.source_url || ""} onChange={(e) => setForm({ ...form, source_url: e.target.value })} data-testid="post-source-url" /></div>
            <div><Label>Source name</Label><Input value={form.source_name || ""} onChange={(e) => setForm({ ...form, source_name: e.target.value })} data-testid="post-source-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Button text</Label><Input value={form.button_text || ""} onChange={(e) => setForm({ ...form, button_text: e.target.value })} data-testid="post-button-text" /></div>
            <div><Label>Button link</Label><Input value={form.button_link || ""} onChange={(e) => setForm({ ...form, button_link: e.target.value })} data-testid="post-button-link" /></div>
          </div>
          <div className="grid grid-cols-1 gap-3">
            <div><Label>Why it matters</Label><Textarea rows={2} value={form.why_it_matters || ""} onChange={(e) => setForm({ ...form, why_it_matters: e.target.value })} data-testid="post-why" /></div>
            <div><Label>Who should care</Label><Input value={form.who_should_care || ""} onChange={(e) => setForm({ ...form, who_should_care: e.target.value })} data-testid="post-who" /></div>
            <div><Label>Next step</Label><Input value={form.next_step || ""} onChange={(e) => setForm({ ...form, next_step: e.target.value })} data-testid="post-next" /></div>
          </div>
          <div>
            <Label>Recommended for (match tags)</Label>
            <div className="flex flex-wrap gap-2 mt-1" data-testid="post-tags">
              {MATCH_TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)} className={`px-3 py-1 rounded-full text-xs font-semibold border ${form.recommended_for?.includes(t) ? "bg-amber-500 text-white border-amber-500" : "bg-white text-slate-600 border-slate-200"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={form.is_featured} onCheckedChange={(v) => setForm({ ...form, is_featured: v })} data-testid="post-featured" /> Featured</label>
            <label className="flex items-center gap-2 text-sm font-semibold"><Switch checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} data-testid="post-published" /> Published</label>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="post-save">{busy ? "Saving…" : isEdit ? "Save changes" : "Create post"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SourcesPanel({ sources, reload }) {
  const [openAdd, setOpenAdd] = useState(false);
  const del = async (id) => { await api.delete(`/admin/feed/sources/${id}`); toast.success("Deleted"); reload(); };
  const toggle = async (s) => { await api.put(`/admin/feed/sources/${s.id}`, { is_active: !s.is_active }); toast.success(s.is_active ? "Disabled" : "Enabled"); reload(); };

  return (
    <Card className="p-4 sm:p-6 rounded-2xl border-slate-100">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Rss size={18} className="text-sky-600" />
          <h3 className="font-display font-bold text-lg">RSS Sources</h3>
        </div>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild><Button size="sm" className="bg-amber-500 hover:bg-amber-600" data-testid="add-source-btn"><Plus size={14} className="mr-1" />Add source</Button></DialogTrigger>
          <AddSourceDialog onSaved={() => { setOpenAdd(false); reload(); }} />
        </Dialog>
      </div>
      <div className="space-y-2">
        {sources.length === 0 && <p className="text-sm text-slate-400">No sources yet.</p>}
        {sources.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100" data-testid={`source-row-${s.id}`}>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 truncate">{s.name}</p>
              <p className="text-xs text-slate-500 truncate">{s.url}</p>
            </div>
            <Badge variant="outline">{CATEGORIES.find(([k]) => k === s.category)?.[1] || s.category}</Badge>
            <label className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Switch checked={s.is_active} onCheckedChange={() => toggle(s)} data-testid={`toggle-source-${s.id}`} /> Active</label>
            <Button size="icon" variant="ghost" className="text-red-600" onClick={() => del(s.id)} data-testid={`delete-source-${s.id}`}><Trash2 size={16} /></Button>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AddSourceDialog({ onSaved }) {
  const [form, setForm] = useState({ name: "", url: "", category: "credit_education", is_active: true });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post("/admin/feed/sources", form); toast.success("Source added"); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <DialogContent className="sm:max-w-md" data-testid="add-source-dialog">
      <DialogHeader><DialogTitle className="font-display text-2xl">New RSS source</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="source-name" /></div>
        <div><Label>RSS/Atom URL</Label><Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://example.com/feed.xml" data-testid="source-url" /></div>
        <div>
          <Label>Default category</Label>
          <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
            <SelectTrigger data-testid="source-category"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <DialogFooter><Button onClick={submit} disabled={busy} className="bg-amber-500 hover:bg-amber-600" data-testid="source-save">{busy ? "Saving…" : "Add source"}</Button></DialogFooter>
    </DialogContent>
  );
}
